import type { DialogueScript, MediaRef, ProjectSnapshot, ShareOptions, User } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

function stripMediaRef(ref: MediaRef | undefined): MediaRef | undefined {
  if (!ref) return undefined;
  if (ref.storage === 'local') return undefined;
  const { blobUrl: _blobUrl, ...rest } = ref;
  return rest;
}

function stripDialogue(dialogue: DialogueScript | undefined): DialogueScript | undefined {
  if (!dialogue) return undefined;
  return {
    avatars: dialogue.avatars.map((avatar) => ({
      ...avatar,
      image: stripMediaRef(avatar.image) ?? { storage: 'builtin', builtinId: 'avatar-1' },
    })),
    lines: dialogue.lines.map((line) => ({
      ...line,
      voice: stripMediaRef(line.voice),
    })),
    hudStyle: dialogue.hudStyle,
  };
}

function withoutLocalMedia(project: ProjectSnapshot): ProjectSnapshot {
  return {
    ...project,
    objects: project.objects.map(({ mediaUrl: _mediaUrl, mediaName: _mediaName, ...object }) => {
      if (object.viewContent?.storage !== 'local') return object;
      return { ...object, viewEnabled: false, viewContent: undefined };
    }),
    dialogue: stripDialogue(project.dialogue),
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string } & T;
  if (!response.ok) throw new Error(payload.error || '요청을 처리하지 못했습니다.');
  return payload;
}

export const api = {
  me: () => request<{ user: User | null }>('/auth/me'),
  register: (body: { name: string; email: string; password: string }) =>
    request<{ user: User }>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    request<{ user: User }>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  deleteAccount: () => request<{ ok: true }>('/auth/account', { method: 'DELETE' }),
  saveProject: (project: ProjectSnapshot) =>
    request<{ project: ProjectSnapshot & { id: string } }>('/projects', {
      method: project.id ? 'PUT' : 'POST',
      body: JSON.stringify(withoutLocalMedia(project)),
    }),
  createShare: (projectId: string, options: ShareOptions) =>
    request<{ url: string; token: string }>(`/projects/${projectId}/share`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),
  readShare: (token: string) =>
    request<{ project: ProjectSnapshot; readOnly: true }>(`/shares/${encodeURIComponent(token)}`),
  uploadModel: (file: File, consent: boolean) => {
    const form = new FormData();
    form.append('file', file);
    form.append('consent', String(consent));
    return request<{ asset: { id: string; name: string; url: string } }>('/models', {
      method: 'POST',
      body: form,
    });
  },
  uploadMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ asset: { id: string; name: string; url: string; contentType: string } }>('/media', {
      method: 'POST',
      body: form,
    });
  },
};
